import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props { children: ReactNode; }
interface State { error: Error | null; info: ErrorInfo | null; }

// Catches render/lifecycle exceptions anywhere below it so a single component throw shows a
// readable error card instead of unmounting the whole app to a blank white screen. The error
// text is surfaced (behind a details toggle) so it can be reported for diagnosis.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the component stack for the details panel and log for anyone watching the console.
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("Unhandled UI error:", error, info.componentStack);
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{ position: "fixed", inset: 0, background: "#f1f5f9", overflowY: "auto", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "8vh 16px", fontFamily: "'Inter','Mulish',sans-serif" }}>
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderLeft: "4px solid #dc2626", borderRadius: 10, maxWidth: 640, width: "100%", padding: "26px 28px", boxShadow: "0 10px 40px rgba(0,0,0,.12)" }}>
          <h1 style={{ margin: "0 0 8px", fontSize: 20, color: "#0f172a", fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: ".7px" }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.6, margin: "0 0 18px" }}>
            The page hit an unexpected error and stopped rendering. Your saved work isn&rsquo;t lost —
            reload to continue. If this keeps happening, copy the technical details below and send them over.
          </p>
          <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: "9px 20px", background: "linear-gradient(135deg,#ea580c,#f97316)", color: "#fff", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}
            >
              ↻ Reload page
            </button>
          </div>
          <details style={{ fontSize: 12, color: "#334155" }}>
            <summary style={{ cursor: "pointer", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".6px", fontSize: 10.5 }}>Technical details</summary>
            <pre style={{ marginTop: 10, padding: "12px 14px", background: "#0f172a", color: "#e2e8f0", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "'Roboto Mono',ui-monospace,monospace" }}>
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ""}
              {info?.componentStack ? `\n\nComponent stack:${info.componentStack}` : ""}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
